/**
 * <CausalAttribution /> — "We don't just show the spike — we show the line."
 *
 * v34.68 (Jun 3 2026) — Nova's R4 #2: Causal attribution down to the line
 * of code. Turns CARTIE from "finance tool" into "essential debugger."
 *
 * Renders an interactive 3-step trace:
 *   Step 1: The $ spike chart with a clickable peak
 *   Step 2: The Causal Bot's verdict ("recursive loop in stream.py")
 *   Step 3: The deep-link straight to the offending line of code in GitHub
 *
 * No backend dependency — pure demo, all sample data clearly labeled.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertOctagon, Code2, GitBranch, ExternalLink, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';

const TRACE_STEPS = [
  {
    id: 'spike',
    icon: AlertOctagon,
    color: 'rose',
    title: 'Cost spike detected',
    detail: 'OpenAI bill jumped 4.7× in 14 minutes',
    timestamp: '14:32 UTC',
    metric: '+$1,847',
  },
  {
    id: 'causal',
    icon: TrendingUp,
    color: 'amber',
    title: 'Causal Bot traces the source',
    detail: '847 nearly-identical prompts from one process. Pattern: recursive completion loop.',
    timestamp: '14:32:09 UTC',
    metric: '99.7% confidence',
  },
  {
    id: 'code',
    icon: Code2,
    color: 'emerald',
    title: 'Line of code identified',
    detail: 'app/api/v1/stream.py · L42',
    timestamp: '14:32:11 UTC',
    metric: 'Auto-PR ready',
  },
];

const CODE_SNIPPET = `# app/api/v1/stream.py · line 42
async def stream_response(prompt: str, depth: int = 0):
    response = await client.chat.completions.create(
        model="gpt-4o",
        messages=[{"role": "user", "content": prompt}],
    )
    text = response.choices[0].message.content
    # ❌ CARTIE FOUND: no depth check — recursion has no exit condition
    if "follow-up" in text.lower():
        return await stream_response(text, depth + 1)  # ← burns $1,847
    return text`;

const FIXED_SNIPPET = `# app/api/v1/stream.py · line 42 (CARTIE Auto-PR fix)
async def stream_response(prompt: str, depth: int = 0):
    # ✅ CARTIE FIX: cap recursion at 3 levels (configurable via settings.MAX_DEPTH)
    if depth >= settings.MAX_DEPTH:
        return prompt
    response = await client.chat.completions.create(
        model="gpt-4o",
        messages=[{"role": "user", "content": prompt}],
    )
    text = response.choices[0].message.content
    if "follow-up" in text.lower():
        return await stream_response(text, depth + 1)
    return text`;

const CausalAttribution = () => {
  const [tab, setTab] = useState('before'); // 'before' | 'after'

  return (
    <section
      className="relative py-16 px-4 bg-slate-950 border-y border-emerald-500/10"
      data-testid="causal-attribution-section"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
            <Sparkles className="w-3 h-3" /> Causal attribution · spike → code in 11 seconds
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-white mb-3">
            We don't just show the spike — <span className="text-emerald-400">we point to the line.</span>
          </h2>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Every other FinOps tool tells you "you spent $10k on tokens." CARTIE tells you "you spent $10k <em className="text-white not-italic">because of line 42 in stream.py</em>" — and writes the fix as a PR.
          </p>
        </div>

        {/* 3-step trace */}
        <div className="grid lg:grid-cols-3 gap-3 mb-8">
          {TRACE_STEPS.map((step, i) => {
            const Icon = step.icon;
            const colorClasses = {
              rose:    { bg: 'bg-rose-500/15', border: 'border-rose-500/40', icon: 'text-rose-300', metric: 'text-rose-300' },
              amber:   { bg: 'bg-amber-500/15', border: 'border-amber-500/40', icon: 'text-amber-300', metric: 'text-amber-300' },
              emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', icon: 'text-emerald-300', metric: 'text-emerald-300' },
            }[step.color];
            return (
              <div key={step.id} className="relative">
                <div
                  className={`rounded-xl border ${colorClasses.border} bg-slate-900 p-5 h-full`}
                  data-testid={`causal-step-${step.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg ${colorClasses.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${colorClasses.icon}`} />
                    </div>
                    <span className="text-[10px] font-mono text-slate-500 tabular-nums">{step.timestamp}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1.5">{step.title}</h3>
                  <p className="text-[12.5px] text-slate-400 leading-relaxed mb-3">{step.detail}</p>
                  <p className={`text-base font-bold ${colorClasses.metric}`}>{step.metric}</p>
                </div>
                {/* Arrow connector */}
                {i < TRACE_STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-slate-700" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Code preview with before/after toggle */}
        <div className="rounded-2xl border border-emerald-500/30 bg-slate-950 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 bg-slate-900/60 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <GitBranch className="w-4 h-4 text-emerald-300" />
              <span className="text-xs font-mono text-slate-300">github.com/your-org/api · pull/2847</span>
            </div>
            <div className="flex gap-1 p-0.5 rounded-lg bg-slate-950 border border-white/5">
              <button
                onClick={() => setTab('before')}
                className={`px-3 py-1 text-[11px] font-mono rounded-md transition-colors ${
                  tab === 'before' ? 'bg-rose-500/20 text-rose-200' : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid="causal-tab-before"
              >
                before · the leak
              </button>
              <button
                onClick={() => setTab('after')}
                className={`px-3 py-1 text-[11px] font-mono rounded-md transition-colors ${
                  tab === 'after' ? 'bg-emerald-500/20 text-emerald-200' : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid="causal-tab-after"
              >
                after · CARTIE fix
              </button>
            </div>
          </div>
          <pre className="px-5 py-5 font-mono text-[12px] sm:text-[13px] text-slate-300 overflow-x-auto leading-relaxed whitespace-pre">
            {tab === 'before' ? CODE_SNIPPET : FIXED_SNIPPET}
          </pre>
          <div className="px-5 py-3 border-t border-white/5 bg-slate-900/40 flex items-center justify-between flex-wrap gap-2">
            <span className="text-[11px] font-mono text-slate-500">
              <span className="text-emerald-300">CARTIE PR #2847</span> · 94% confidence · auto-mergeable
            </span>
            <a
              href="https://github.com/cartieai"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-300 hover:text-emerald-200 transition-colors"
              data-testid="causal-github-link"
            >
              view full PR <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/anomaly-autopsy"
            className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-semibold text-sm transition-colors"
            data-testid="causal-cta-autopsy"
          >
            See more autopsy examples → token leaks, idle GPUs, recursive loops
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-[11px] text-slate-500 mt-3">
            Sample data shown. On your account, every spike links to your actual GitHub commit + your engineer who owns the file.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CausalAttribution;
